<script lang="ts">
/** The tone of one line of a `ZhEventLog`. */
export type ZhEventLogTone = 'pending' | 'done' | 'failed'

/** One line of a `ZhEventLog`: its own id, its words and its tone. */
export interface ZhEventLogLine {
    id: string | number
    text: string
    tone: ZhEventLogTone
}
</script>

<script setup lang="ts">
/**
 * An ordered log of events as they happen: the assembly of a team setup clones, line by line. It shows exactly the
 * lines it is given, in that order, and never makes one up: what writes a line is the caller's source of events.
 *
 * - `role="log"` with polite live updates, so a screen reader reads each new line once, without interrupting.
 * - Each line carries its tone (`pending`, `done` or `failed`) as `data-tone`, drawn from the theme's state tokens.
 * - A failed line has an `action` slot (a Retry, say), given the line; no other line does.
 * - Lines are keyed by `id`, so a new line is added without redrawing the ones before it.
 */
withDefaults(
    defineProps<{
        lines: ReadonlyArray<ZhEventLogLine>
        /** The log's accessible name. */
        label?: string
    }>(),
    { label: 'Event log' },
)

defineSlots<{
    action?: (props: { line: ZhEventLogLine }) => unknown
}>()
</script>

<template>
    <ol class="zh-event-log" role="log" aria-live="polite" :aria-label="label">
        <li v-for="line in lines" :key="line.id" class="zh-event-log__line" :data-tone="line.tone">
            <span class="zh-event-log__mark" aria-hidden="true" />
            <span class="zh-event-log__text">{{ line.text }}</span>
            <span v-if="line.tone === 'failed' && $slots.action" class="zh-event-log__action">
                <slot name="action" :line="line" />
            </span>
        </li>
    </ol>
</template>

<style scoped>
.zh-event-log {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    list-style: none;
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    color: var(--dirty-white);
}

.zh-event-log__line {
    display: flex;
    align-items: center;
    gap: 0.625rem;
}

.zh-event-log__mark {
    flex: none;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 9999px;
    background: var(--state-inactive);
}

.zh-event-log__line[data-tone='pending'] .zh-event-log__mark {
    background: var(--state-active);
}

.zh-event-log__line[data-tone='done'] .zh-event-log__mark {
    background: var(--state-complete);
}

.zh-event-log__line[data-tone='failed'] .zh-event-log__mark {
    background: var(--state-failed);
}

.zh-event-log__line[data-tone='failed'] .zh-event-log__text {
    color: var(--state-failed);
}

.zh-event-log__text {
    flex: 1 1 auto;
    min-width: 0;
}

.zh-event-log__action {
    flex: none;
}
</style>
